import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { UserContext } from '../UserContext';
import './GroupPage.css';
import { formatDate } from '../utils/formatDate';

const API_BASE_URL = 'https://wholeness-forward.onrender.com/api';

const GroupPage = () => {
    const { user } = useContext(UserContext);
    const { groupId } = useParams();
    
    const [group, setGroup] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timeframe, setTimeframe] = useState('allTime');
    const [threads, setThreads] = useState([]);
    const [newThreadTitle, setNewThreadTitle] = useState('');
    const [newThreadContent, setNewThreadContent] = useState('');
    const [commentContent, setCommentContent] = useState('');
    const [replyContent, setReplyContent] = useState('');
    const [forumError, setForumError] = useState(null);
    const [forumLoading, setForumLoading] = useState(false);
    const [reactions, setReactions] = useState({});
    const [expandedThreads, setExpandedThreads] = useState({});
    const [expandedComments, setExpandedComments] = useState({});
    const [isCommentInputVisible, setIsCommentInputVisible] = useState({});
    const [showNewThreadForm, setShowNewThreadForm] = useState(false);
    const [editingThread, setEditingThread] = useState(null);
    const [editingThreadContent, setEditingThreadContent] = useState('');
    const [editingComment, setEditingComment] = useState(null);
    const [editingCommentContent, setEditingCommentContent] = useState('');
    const [editingReply, setEditingReply] = useState(null);
    const [editingReplyContent, setEditingReplyContent] = useState('');
    const logoPath = "/images/gratiSphere.svg";


    const validReactions = [
        { type: 'like', path: '/likedark.svg', selectedPath: '/likeselected.svg' },
        { type: 'love', path: '/lovedark.svg', selectedPath: '/loveselected.svg' },
        { type: 'insightful', path: '/insightfuldark.svg', selectedPath: '/insightfulselected.svg' },
      ];
      
      const getReactionImage = (reactionType, selected) => {
          const reaction = validReactions.find(r => r.type === reactionType);
          return reaction ? (selected ? reaction.selectedPath : reaction.path) : '/likedark.svg'; // Default fallback
      };

    
    // ✅ Convert grams to pounds & format correctly
    const convertWeight = (grams) => {
        if (grams === null || grams === undefined) return "0.0"; 
        const lbs = (grams / 453.592).toFixed(1);
        return lbs;
    };

    // ✅ Fetch Group Details with Timeframe
    useEffect(() => {
        const fetchGroup = async () => {
            if (!user || !groupId) return;
    
            setLoading(true);
            setError(null);
    
            try {
                const response = await axios.get(`${API_BASE_URL}/group/${groupId}`, {
                    headers: { Authorization: `Bearer ${user?.token}` },
                    params: { timeframe } // Add the timeframe to the request if needed
                });
    
                console.log("✅ Group Data Received:", response.data);
    
                // Process members' weight history
                const processedMembers = response.data.members.map((member) => {
                    const userData = member.userId || {}; // Ensure `userId` is always defined
                    const groupStartWeightG = member.groupStartWeightG ?? 0; // Group start weight
                
                    const weightHistory = Array.isArray(member.weightHistory) ? member.weightHistory : [];
                    const latestWeightG = weightHistory.length > 0 ? weightHistory[weightHistory.length - 1].weightG : groupStartWeightG;
                    
                    // ✅ Fix: Get the previous weight correctly from weight history
                    const previousWeightG = weightHistory.length > 1 ? weightHistory[weightHistory.length - 2].weightG : latestWeightG;
                
                    // ✅ Calculate weight lost from group start
                    const weightLostG = groupStartWeightG - latestWeightG;
                
                    // ✅ Calculate weight change since the last update
                    const weightChangeSinceLastUpdateG = latestWeightG - previousWeightG;
                
                    return {
                        ...member,
                        latestWeightG,
                        weightLost: convertWeight(weightLostG), 
                        weightChangeSinceLastUpdate: convertWeight(weightChangeSinceLastUpdateG), 
                        startingWeight: convertWeight(groupStartWeightG),
                        name: userData.username || member.name || "Unknown",
                    };
                });
                
    
                setGroup({
                    ...response.data,
                    members: processedMembers,
                });
    
            } catch (err) {
                console.error("❌ Error fetching group:", err.response?.data || err.message);
                setError(err.response?.data?.error || "Group not found.");
            } finally {
                setLoading(false);
            }
        };


        const fetchThreads = async () => {
            setForumLoading(true);
            try {
                const response = await axios.get(`${API_BASE_URL}/groupForum/${groupId}/threads`);
                const updatedThreads = await fetchCommentsAndReplies(response.data);
        
                // Ensure reactions are included for each thread, comment, and reply
                setThreads(updatedThreads);
                // Optional: Set reactions state with the initial values from the backend
                const initialReactions = updatedThreads.reduce((acc, thread) => {
                    acc[thread._id] = thread.reactions || {};
                    thread.comments.forEach((comment) => {
                        acc[comment._id] = comment.reactions || {};
                        comment.replies.forEach((reply) => {
                            acc[reply._id] = reply.reactions || {};
                        });
                    });
                    return acc;
                }, {});
                setReactions(initialReactions); // Set reactions in the state
            } catch (error) {
                console.error('❌ Error fetching threads:', error);
                setForumError('Failed to load threads.');
            } finally {
                setForumLoading(false);
            }
        };
        

        const fetchCommentsAndReplies = async (threads) => {
            try {
                const updatedThreads = await Promise.all(
                    threads.map(async (thread) => {
                        const response = await axios.get(`${API_BASE_URL}/groupForum/${thread._id}/comments`);
                        const comments = response.data || [];

                        const updatedComments = await Promise.all(
                            comments.map(async (comment) => {
                                const repliesResponse = await axios.get(
                                    `${API_BASE_URL}/groupForum/${thread._id}/${comment._id}/replies`
                                );
                                comment.replies = repliesResponse.data || [];
                                return comment;
                            })
                        );

                        thread.comments = updatedComments;
                        return thread;
                    })
                );
                return updatedThreads;
            } catch (error) {
                console.error('❌ Error fetching comments & replies:', error);
                return threads;
            }
        };

        if (user && groupId) {
            fetchGroup();
            fetchThreads();
        }
    }, [user, groupId, timeframe ]);

    // ✅ Compute Group Total Weight Lost (No Timeframe)
    const getTotalWeightLost = () => {
        if (!group) return "0.0"; // Default if no data

        // Use the total weight lost directly from group data (no timeframe)
        const weightChangeG = group.totalWeightLostG; 

        const weightChangeLbs = convertWeight(Math.abs(weightChangeG)); // Convert from grams to pounds

        // ✅ Show weight change with correct sign: "-" for loss, "+" for gain
        return weightChangeG > 0 ? `-${weightChangeLbs}` : `+${weightChangeLbs}`;
    };




    const handlePostThread = async (e) => {
        e.preventDefault();
        if (!user) {
            setForumError('You must be logged in to post a thread.');
            return;
        }
    
        setForumLoading(true);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/groupForum/${groupId}/${user.id}/threads`,
                { title: newThreadTitle, content: newThreadContent },
                { headers: { Authorization: `Bearer ${user.token}` } }
            );
    
            setThreads((prevThreads) => [response.data, ...prevThreads]); // ✅ Add new thread to the list
            setNewThreadTitle('');
            setNewThreadContent('');
        } catch (error) {
            console.error('❌ Error posting thread:', error);
            setForumError('Failed to post thread.');
        } finally {
            setForumLoading(false);
        }
    };
    
    
    const handlePostComment = async (threadId) => {
        if (!commentContent[threadId]?.trim()) return;
    
        try {
            const response = await axios.post(
                `${API_BASE_URL}/groupForum/${threadId}/${user.id}/comments`,
                { content: commentContent[threadId] },
                { headers: { Authorization: `Bearer ${user.token}` } }
            );
    
            setThreads((prevThreads) =>
                prevThreads.map((thread) =>
                    thread._id === threadId
                        ? { ...thread, comments: [response.data, ...thread.comments] }
                        : thread
                )
            );
    
            setCommentContent((prev) => ({ ...prev, [threadId]: '' }));
        } catch (error) {
            console.error('❌ Error adding comment:', error);
        }
    };
    

    const handlePostReply = async (threadId, commentId) => {
        if (!replyContent[commentId]?.trim()) return;

        try {
            await axios.post(
                `${API_BASE_URL}/groupForum/${threadId}/${commentId}/${user.id}/replies`,
                { content: replyContent[commentId] },
                { headers: { Authorization: `Bearer ${user.token}` } }
            );
            setReplyContent((prev) => ({ ...prev, [commentId]: '' }));
            window.location.reload(); // Refresh to fetch new replies
        } catch (error) {
            console.error('❌ Error adding reply:', error);
        }
    };

    const handleReaction = async (postType, postId, reactionType) => {
        if (!user) {
            alert("You must be logged in to react!");
            return;
        }
    
        try {
            const response = await axios.post(
                `${API_BASE_URL}/groupForum/${postType}/${postId}/${user.id}/reaction/${reactionType}`,
                {},
                { headers: { Authorization: `Bearer ${user.token}` } }
            );
    
            // Update the reactions state with the latest data returned from the backend
            setReactions((prevReactions) => ({
                ...prevReactions,
                [postId]: response.data.reactions, // Update the reaction counts from the backend
            }));
    
            // Optional: Log the image path for debugging
            getReactionImage(reactionType); 
            
        } catch (error) {
            console.error("Error updating reaction:", error);
        }
    };
    
        // ✅ Toggle function for new thread form
    const toggleNewThreadForm = () => {
        setShowNewThreadForm((prev) => !prev);
    };
        

    const toggleCommentInputVisibility = (threadId) => {
        setIsCommentInputVisible((prevState) => ({
            ...prevState,
            [threadId]: !prevState[threadId],
        }));
    };

    if (loading) return <p className="loading">🔄 Loading group details...</p>;
    if (error) return <p className="error">❌ {error}</p>;
    if (!group) return <p className="no-group">⚠️ No group found.</p>;

    const toggleThreadExpand = (threadId) => {
        setExpandedThreads((prev) => ({ ...prev, [threadId]: !prev[threadId] }));
    };

    const toggleCommentExpand = (commentId) => {
        setExpandedComments((prev) => ({ ...prev, [commentId]: !prev[commentId] }));
    };

    const canEditOrDelete = (postUserId) => {
        if (!user || !postUserId) return false;
      
        // Normalize user IDs for comparison
        const userIdString = typeof postUserId === "object" ? postUserId._id?.toString() : postUserId.toString();
        
        return userIdString === user.id.toString();
      };
      
    
    const handleEditThread = (thread) => {
        setEditingThread(thread._id);
        setEditingThreadContent(thread.content);
    };
    
    const saveEditedThread = async (threadId) => {
        try {
            await axios.put(
                `${API_BASE_URL}/groupForum/${groupId}/${threadId}/${user.id}`, 
                { content: editingThreadContent }, // ✅ Use thread-specific state
                { headers: { Authorization: `Bearer ${user.token}` } }
            );
    
            setThreads((prevThreads) =>
                prevThreads.map((thread) =>
                    thread._id === threadId ? { ...thread, content: editingThreadContent } : thread
                )
            );
    
            setEditingThread(null); // ✅ Exit edit mode
        } catch (error) {
            console.error("❌ Error updating thread:", error);
        }
    };
    
    const handleEditComment = (comment) => {
        setEditingComment(comment._id);
        setEditingCommentContent(comment.content);
    };

    const handleEditReply = (reply) => {
        setEditingReply(reply._id);
        setEditingReplyContent(reply.content);
    };

    const saveEditedComment = async (threadId, commentId) => {
        try {console.log("🔹 Sending Edit Comment Request:", { threadId, commentId, content: editingCommentContent });

            const response = await axios.put(
                `${API_BASE_URL}/groupForum/comments/${threadId}/${commentId}/${user.id}`,
                { content: editingCommentContent }, // ✅ Use comment-specific state
                { headers: { Authorization: `Bearer ${user.token}` } }
            );
    
            console.log("✅ Comment Updated:", response.data);
    
            // ✅ Update state to reflect the new comment content from backend
            setThreads((prevThreads) =>
                prevThreads.map((thread) =>
                    thread._id === threadId
                        ? {
                            ...thread,
                            comments: thread.comments.map((comment) =>
                                comment._id === commentId 
                                    ? { ...comment, content: editingCommentContent } // ✅ Correctly update comment
                                    : comment
                            ),
                        }
                        : thread
                )
            );
    
            setEditingComment(null); // ✅ Exit edit mode
        } catch (error) {
            console.error("❌ Error updating comment:", error.response?.data || error.message);
        }
    };
    
    
    
    const saveEditedReply = async (threadId, commentId, replyId) => {
        try {
            await axios.put(
                `${API_BASE_URL}/groupForum/${threadId}/${commentId}/${replyId}/${user.id}`, 
                { content: editingReplyContent }, // ✅ Use reply-specific state
                { headers: { Authorization: `Bearer ${user.token}` } }
            );
    
            setThreads((prevThreads) =>
                prevThreads.map((thread) =>
                    thread._id === threadId
                        ? {
                            ...thread,
                            comments: thread.comments.map((comment) =>
                                comment._id === commentId
                                    ? {
                                        ...comment,
                                        replies: comment.replies.map((reply) =>
                                            reply._id === replyId ? { ...reply, content: editingReplyContent } : reply
                                        ),
                                    }
                                    : comment
                            ),
                        }
                        : thread
                )
            );
    
            setEditingReply(null); // ✅ Exit edit mode
        } catch (error) {
            console.error("❌ Error updating reply:", error);
        }
    };
    
    const deleteThread = async (threadId) => {
        try {
            await axios.delete(
                `${API_BASE_URL}/groupForum/comments/${groupId}/${threadId}/${user.id}`, 
                { headers: { Authorization: `Bearer ${user.token}` } }
            );
    
            // ✅ Instead of removing the thread, mark it as deleted
            setThreads((prevThreads) =>
                prevThreads.map((thread) =>
                    thread._id === threadId
                        ? { ...thread, content: "Deleted by user", username: "[Deleted User]" }
                        : thread
                )
            );
        } catch (error) {
            console.error("❌ Error deleting thread:", error);
        }
    };
    
    const deleteComment = async (threadId, commentId) => {
        console.log("🗑 Soft Deleting Comment:", { threadId, commentId });
    
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/groupForum/comments/${threadId}/${commentId}/${user.id}`, 
                { headers: { Authorization: `Bearer ${user.token}` } }
            );
    
            console.log("✅ Comment Soft Deleted:", response.data);
    
            // ✅ Instead of removing, mark it as deleted
            setThreads((prevThreads) =>
                prevThreads.map((thread) =>
                    thread._id === threadId
                        ? {
                            ...thread,
                            comments: thread.comments.map((comment) =>
                                comment._id === commentId
                                    ? { ...comment, content: "Deleted by user", username: "[Deleted User]" }
                                    : comment
                            ),
                        }
                        : thread
                )
            );
    
        } catch (error) {
            console.error("❌ Error soft deleting comment:", error.response?.data || error.message);
        }
    };
    
    
    
    
    const deleteReply = async (threadId, commentId, replyId) => {
        try {
            await axios.delete(
                `${API_BASE_URL}/groupForum/${threadId}/${commentId}/${replyId}/${user.id}`, 
                { headers: { Authorization: `Bearer ${user.token}` } }
            );
    
            // ✅ Instead of removing the reply, mark it as deleted
            setThreads((prevThreads) =>
                prevThreads.map((thread) =>
                    thread._id === threadId
                        ? {
                            ...thread,
                            comments: thread.comments.map((comment) =>
                                comment._id === commentId
                                    ? {
                                        ...comment,
                                        replies: comment.replies.map((reply) =>
                                            reply._id === replyId
                                                ? { ...reply, content: "Deleted by user", username: "[Deleted User]" }
                                                : reply
                                        ),
                                    }
                                    : comment
                            ),
                        }
                        : thread
                )
            );
        } catch (error) {
            console.error("❌ Error deleting reply:", error);
        }
    };
    

    return (
        <div className="group-page-container">
            <div className="group-header">
                <h1 className="group-title">{group.name}</h1>
            </div>
            <hr className="section-divider" />
            
            <div className="leaderboard-section">
        <div className="container">
        <img src={logoPath} alt="GratiSphere Logo" className="leader-logo" />
        </div>
        <h3 className="leader-centered">GratiSphere Group Leaderboard</h3>
        <div className="micro-void"></div>

        <table className="mini-leaderboard-table">
            <thead>
                <tr>
                    <th>Rank</th>
                    <th>Name</th>
                    <th>Current Streak🔥</th>
                    <th>Longest Streak <img src={logoPath} alt="GratiSphere Logo" className="icon-small" /></th>
                    <th>Total Days 📅</th>
                </tr>
            </thead>
            <tbody>
                {group.members.length === 0 ? (
                    <tr><td colSpan="5">No data available</td></tr>
                ) : (
                    [...group.members]
                        .sort((a, b) => b.gratitudeStats?.currentStreak - a.gratitudeStats?.currentStreak) // Sort by highest streak
                        .map((member, index) => (
                            <tr key={index}>
                                <td># {index + 1}</td>
                                <td>{member.name}</td>
                                <td>{member.gratitudeStats?.currentStreak || 0} 🔥</td>
                                <td>{member.gratitudeStats?.longestStreak || 0} <img src={logoPath} alt="GratiSphere Logo" className="icon-small" /></td>
                                <td>{member.gratitudeStats?.totalDaysRecorded || 0} 📅</td>
                            </tr>
                        ))
                )}
            </tbody>
        </table>
        <div className="leaderboard-summary">
            <div className="micro-void"></div>
        <h5 className="h5-custom">GratiSphere Group Averages:</h5>
    <p>🔥 Current Streak Avg: <strong>{group.currentGratitudeStreakAvg.toFixed(1)}</strong></p>
    <p><img src={logoPath} alt="GratiSphere Logo" className="icon-smaller" /> Longest Streak Avg: <strong>{group.longestGratitudeStreakAvg.toFixed(1)}</strong></p>
    <p>📅 Group Total Days: <strong>{group.totalGratitudeDaysSum.toLocaleString()}</strong></p>
    <div className="micro-void"></div>
</div>
    </div>

            <hr className="section-divider" />

            <div className="leaderboard-section">
            <div className="container">
      <img src="/images/weighlogo.png" alt="WeighForward Logo" className="leader-logo" />
        </div>
                <h3 className="leader-centered">WeighForward Group Leaderboard</h3>
                <div className="micro-void"></div>

                          {/* Timeframe Dropdown */}
                          <div className="timeframe-selector">
                <label>Show data for:</label>
                <select value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                    <option value="allTime">All Time</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="year">This Year</option>
                </select>
            </div>

                <table className="mini-leaderboard-table">
                    <thead>
                        <tr>
                            <th>Rank</th>
                            <th>Name</th>
                            <th>Weight Change (lbs)</th>
                            <th>Since Last Update (lbs)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {group.members.length === 0 ? (
                            <tr><td colSpan="4">No data available</td></tr>
                        ) : (
                            [...group.members]
                                .sort((a, b) => parseFloat(b.weightLost) - parseFloat(a.weightLost)) // ✅ Sort numerically
                                .map((member, index) => (
                                    <tr key={index}>
                                        <td># {index + 1}</td>
                                        <td>{member.name}</td>
                                        <td className={
                                            parseFloat(member.weightLost) < 0 ? "gain" :
                                            parseFloat(member.weightLost) > 0 ? "loss" :
                                            "neutral"
                                        }>
                                            {member.weightLost !== "N/A" ? `${member.weightLost} lbs` : "N/A"}
                                        </td>
                                        <td className={
                                            parseFloat(member.weightChangeSinceLastUpdate) > 0 ? "gain" :
                                            parseFloat(member.weightChangeSinceLastUpdate) < 0 ? "loss" :
                                            "neutral"
                                        }>
                                            {member.weightChangeSinceLastUpdate !== "N/A" ? `${member.weightChangeSinceLastUpdate} lbs` : "N/A"}
                                        </td>
                                    </tr>
                                ))
                        )}
                    </tbody>
                </table>
                <div className="mini-void"></div>
                <h5 className="h5-custom">
                    Group Total Change: {}
                    <span className={
                        parseFloat(getTotalWeightLost()) > 0 ? "gain" :
                        parseFloat(getTotalWeightLost()) < 0 ? "loss" :
                        "neutral"
                    }>
                        {getTotalWeightLost()} lbs
                    </span>
                </h5>

                <Link to="/group-leaderboard" className="forum-button-leader">View full group leaderboard</Link>
            </div>
            <hr className="section-divider" />
            <div className="join-code-section">
                <p><strong>Members</strong> ({group.members.length}/{group.maxMembers})</p>
                {group.members.length > 0 ? (
                    <ul className="member-list">
                        {group.members.map((member) => (
                            <li key={member.userId?._id ?? member.name}> {member.name}</li>
                        ))}
                    </ul>
                ) : (
                    <p>❌ No members yet.</p>
                )}
            </div>

            <div className="group-info">
                <div className="micro-void"></div>
                <p><strong>Admin:</strong> <span className="admin-name">{group.adminName}</span></p>
                <p><strong>Max Members:</strong> {group.maxMembers}</p>
            </div>
            <div className="mini-void"></div>

            <hr className="section-divider" />
           
            
{/* Forum Threads */}
<div className="group-forum-section">
    <h3 className="group-chat-title">Group Discussion</h3>

    {user && (
    <div className="forum-new-thread">
        <button 
            className="forum-button" 
            onClick={toggleNewThreadForm}
        >
            {showNewThreadForm ? "Hide Thread Form" : "Post a New Thread"}
        </button>

        <div className="mini-void"></div>

        {showNewThreadForm && (
            <div className="new-thread-form">
                <h4>Post a New Thread</h4>
                <form onSubmit={handlePostThread}>
                    <input
                        type="text"
                        className="forum-input-title"
                        placeholder="Thread Title"
                        value={newThreadTitle}
                        onChange={(e) => setNewThreadTitle(e.target.value)}
                    />
                    <textarea
                        className="forum-input-content"
                        placeholder="What's on your mind?"
                        value={newThreadContent}
                        onChange={(e) => setNewThreadContent(e.target.value)}
                    />
                    <button type="submit" className="forum-button" disabled={forumLoading}>
                        {forumLoading ? 'Posting...' : 'Post Thread'}
                    </button>
                </form>
            </div>
        )}
    </div>
)}


    {forumError && <p className="forum-error">{forumError}</p>}

    {threads.map((thread) => (
    <div key={thread._id} className="forum-thread-card">
        <h5>{thread.title}</h5>
        <p>{thread.content}</p>
        <p className="thread-author">{thread.username} - {formatDate(thread.createdAt)}</p>

             {/* ✅ Only show Edit/Delete buttons for the thread owner */}
             {canEditOrDelete(thread.user) && (
            <div className="edit-delete-buttons">
                {editingThread === thread._id ? (
                    <>
                        <textarea 
                            value={editingThreadContent} 
                            onChange={(e) => setEditingThreadContent(e.target.value)}
                        />
                        <button className="forum-button" onClick={() => saveEditedThread(thread._id)}>Save</button>
                        <button className="forum-button-secondary" onClick={() => setEditingThread(null)}>Cancel</button>
                    </>
                ) : (
                    <>
                        <button className="forum-button-outline" onClick={() => handleEditThread(thread)}>Edit</button>
                        <button className="forum-button-secondary" onClick={() => deleteThread(thread._id)}>Delete</button>
                    </>
                )}
            </div>
        )}

        {/* Reactions for Threads */}
        <div className="reactions-section">
            {validReactions.map(({ type }) => (
                <button 
                    key={type} 
                    onClick={() => handleReaction('thread', thread._id, type)} 
                    className="reaction-button"
                >
                    <img 
                        src={getReactionImage(type, reactions[thread._id]?.[type]?.includes(user?.id))} 
                        alt={type.charAt(0).toUpperCase() + type.slice(1)} 
                        className="reaction-icon" 
                    />
                    <span>{reactions[thread._id]?.[type]?.length || 0}</span>
                </button>
            ))}
        </div>

   


{/* Toggle Comments */}
<button className="forum-button" onClick={() => toggleThreadExpand(thread._id)}>
    {expandedThreads[thread._id] ? `Hide Comments (${thread.comments.length})` : `See Comments (${thread.comments.length})`}
</button>

{/* Show Comments if expanded */}
{expandedThreads[thread._id] && (
    <div className="comments-section">
        {thread.comments.map((comment) => (
            <div key={comment._id} className="comment-card">
                
                {/* Comment Content (Editable if owner) */}
                {editingComment === comment._id ? (
                    <>
                        <textarea 
                            value={editingCommentContent} 
                            onChange={(e) => setEditingCommentContent(e.target.value)}
                        />
                        <button className="forum-button" onClick={() => saveEditedComment(thread._id, comment._id)}>Save</button>
                        <button className="forum-button-secondary" onClick={() => setEditingComment(null)}>Cancel</button>
                    </>
                ) : (
                    <>
                        <p>{comment.content}</p>
                        <p className="comment-author">{comment.username} - {formatDate(comment.createdAt)}</p>
                    </>
                )}

                {/* ✅ Only show Edit/Delete buttons for the comment owner */}
                {canEditOrDelete(comment.user) && (
                    <div className="edit-delete-buttons">
                        {editingComment === comment._id ? null : (
                            <>
                                <button className="forum-button-outline" onClick={() => handleEditComment(comment)}>Edit</button>
                                <button className="forum-button-secondary" onClick={() => deleteComment(thread._id, comment._id)}>Delete</button>
                            </>
                        )}
                    </div>
                )}

                {/* Reactions for Comments */}
                <div className="reactions-section">
                    {validReactions.map(({ type }) => (
                        <button 
                            key={type} 
                            onClick={() => handleReaction('comment', comment._id, type)} 
                            className="reaction2-button"
                        >
                            <img 
                                src={getReactionImage(type, reactions[comment._id]?.[type]?.includes(user?.id))} 
                                alt={type.charAt(0).toUpperCase() + type.slice(1)} 
                                className="reaction-icon" 
                            />
                            <span>{reactions[comment._id]?.[type]?.length || 0}</span>
                        </button>
                    ))}
                </div>

                {/* ✅ Toggle Replies Button */}
                <button className="forum-button" onClick={() => toggleCommentExpand(comment._id)}>
                    {expandedComments[comment._id] ? `Hide Replies (${comment.replies.length})` : `See Replies (${comment.replies.length})`}
                </button>

                {/* Replies Section (Displayed Together) */}
                {expandedComments[comment._id] && (
                    <div className="replies-container">
                        {comment.replies.map((reply) => (
                            <div key={reply._id} className="reply-card">
                                
                                {/* Reply Content (Editable if owner) */}
                                {editingReply === reply._id ? (
                                    <>
                                        <textarea 
                                            value={editingReplyContent} 
                                            onChange={(e) => setEditingReplyContent(e.target.value)}
                                        />
                                        <button className="forum-button" onClick={() => saveEditedReply(thread._id, comment._id, reply._id)}>Save</button>
                                        <button className="forum-button-secondary" onClick={() => setEditingReply(null)}>Cancel</button>
                                    </>
                                ) : (
                                    <>
                                        <p>{reply.content}</p>
                                        <p className="reply-author">{reply.username} - {formatDate(reply.createdAt)}</p>
                                    </>
                                )}

                                {/* ✅ Only show Edit/Delete buttons for the reply owner */}
                                {canEditOrDelete(reply.user) && (
                                    <div className="edit-delete-buttons">
                                        {editingReply === reply._id ? null : (
                                            <>
                                                <button className="forum-button-outline" onClick={() => handleEditReply(reply)}>Edit</button>
                                                <button className="forum-button-secondary" onClick={() => deleteReply(thread._id, comment._id, reply._id)}>Delete</button>
                                            </>
                                        )}
                                        <div className="mini-void"></div>
                                    </div>
                                    
                                )}

                                {/* Reactions for Replies */}
                                <div className="reactions-section">
                                    {validReactions.map(({ type }) => (
                                        <button 
                                            key={type} 
                                            onClick={() => handleReaction('reply', reply._id, type)} 
                                            className="reaction2-button"
                                        >
                                            <img 
                                                src={getReactionImage(type, reactions[reply._id]?.[type]?.includes(user?.id))} 
                                                alt={type.charAt(0).toUpperCase() + type.slice(1)} 
                                                className="reaction-icon" 
                                            />
                                            <span>{reactions[reply._id]?.[type]?.length || 0}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* Input to Post a Reply */}
                        {user && (
                            <div>
                                <textarea
                                    className="reply-input"
                                    placeholder="Write a reply..."
                                    value={replyContent[comment._id] || ''}
                                    onChange={(e) =>
                                        setReplyContent((prev) => ({ ...prev, [comment._id]: e.target.value }))
                                    }
                                />
                                <button className="forum-button" onClick={() => handlePostReply(thread._id, comment._id)}>Submit Reply</button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        ))}
    </div>
)}

{/* Toggle Comment Input Visibility */}
<button className="forum-button-secondary" onClick={() => toggleCommentInputVisibility(thread._id)}>
    {isCommentInputVisible[thread._id] ? 'Hide Comment Box' : 'Add a Comment'}
</button>

{/* Comment Input Box */}
{isCommentInputVisible[thread._id] && (
    <div>
        <textarea
            placeholder="Add a comment..."
            value={commentContent[thread._id] || ''}
            onChange={(e) =>
                setCommentContent((prev) => ({ ...prev, [thread._id]: e.target.value }))
            }
        />
        <button className="forum-button" onClick={() => handlePostComment(thread._id)}>Submit Comment</button>
    </div>
)}

        </div>
    ))}
</div>
        </div>
    );  
}



export default GroupPage;
